import * as React from 'react';
import { useDidMountEffect, useDidUpdateEffect } from '@rocketmakers/armstrong';
import { ApiHooksCaching } from './caching';
import { ApiHooksStore } from './store';
import { ApiHooksSystemSettings } from './systemSettings';
import { ApiHooksGlobal } from './global';
import { EndpointIDs } from './endpointIDs';

const isFormData = (params: any) => params instanceof FormData;

/**
 * API Hooks
 * ----------------
 * Parsing of the generated API client into a set of hooks.
 *
 * This file contains everything relating to the hooks themselves, includes:
 * - The hook creation entry point
 * - The useQuery and useMutation hooks
 * - The creation of the empty mock endpoint + default settings dictionaries
 */
export namespace ApiHooks {
  /** UTILITY TYPES */

  /** general utility type - represents a function  */
  type AnyFunction = (param: any) => any;

  /** general utility type - gets the type of a promise result from a promise */
  type PromiseResult<TPromise> = TPromise extends Promise<infer TResult> ? TResult : never;

  /** general utility type - gets the type of the first parameter in a function */
  type FirstParamOf<TFunc extends AnyFunction> = Parameters<TFunc>[0];

  /** CLIENT CREATION TYPES */

  /** The type for the factory function that creates the hook config library */
  export type HookConfigLibraryFactory<TApiClient> = (
    emptyLibrary: HookConfigControllerLibrary<TApiClient>
  ) => HookConfigControllerLibrary<TApiClient>;

  /** The type for the factory function that creates the mock endpoint library */
  export type MockEndpointLibraryFactory<TApiClient> = (
    emptyLibrary: MockEndpointControllerLibrary<TApiClient>
  ) => MockEndpointControllerLibrary<TApiClient>;

  /** The type for the factory function that creates the mock endpoint library */
  export type DefaultDataLibraryFactory<TApiClient> = (
    emptyLibrary: DefaultDataControllerLibrary<TApiClient>
  ) => DefaultDataControllerLibrary<TApiClient>;

  /** The root type of the apiHooks object, represents a dictionary of controllers */
  type ControllerHooks<TApiClient> = {
    [TControllerKey in keyof TApiClient]: EndpointHooks<TApiClient[TControllerKey]>;
  };

  /** The root type of the hookConfig object, represents a dictionary of controllers */
  type HookConfigControllerLibrary<TApiClient> = {
    [TControllerKey in keyof TApiClient]: HookEndpointConfig<TApiClient[TControllerKey]>;
  };

  /** The root type of the mockEndpoints object, represents a dictionary of controllers */
  type MockEndpointControllerLibrary<TApiClient> = {
    [TControllerKey in keyof TApiClient]: MockEndpointLibrary<TApiClient[TControllerKey]>;
  };

  /** The root type of the defaultData object, represents a dictionary of controllers */
  type DefaultDataControllerLibrary<TApiClient> = {
    [TControllerKey in keyof TApiClient]: DefaultDataLibrary<TApiClient[TControllerKey]>;
  };

  /**
   * Adds the three hooks to each endpoint within a controller (if it's a function within a controller, it's an endpoint)
   * @param useQuery The hook to be used if the endpoint is a GET and should interface with the caching system
   * @param useMutation The hook to be used if the endpoint is a POST/PUT/DELETE and we just need a fetcher and sone live, local state. without any caching.
   * @param useRequest The hook to be used for any request when we ONLY want the basic promise constructor, with no local state and no caching at all.
   */
  type EndpointHooks<TApiController> = {
    [TEndpointKey in keyof TApiController]: TApiController[TEndpointKey] extends AnyFunction
      ? {
          useQuery: UseQuery<TApiController[TEndpointKey]>;
          useMutation: UseMutation<TApiController[TEndpointKey]>;
          useRequest: UseRequest<TApiController[TEndpointKey]>;
        }
      : never;
  };

  /**
   * Adds the mock endpoint constructor to each endpoint within a controller (if it's a function within a controller, it's an endpoint)
   */
  type MockEndpointLibrary<TApiController> = {
    [TEndpointKey in keyof TApiController]: TApiController[TEndpointKey] extends AnyFunction
      ? (arg: FirstParamOf<TApiController[TEndpointKey]>, testKey: string) => ReturnType<TApiController[TEndpointKey]>
      : never;
  };

  /**
   * Adds the default data constructor to each endpoint within a controller (if it's a function within a controller, it's an endpoint)
   */
  type DefaultDataLibrary<TApiController> = {
    [TEndpointKey in keyof TApiController]: TApiController[TEndpointKey] extends AnyFunction
      ? (arg: FirstParamOf<TApiController[TEndpointKey]>) => PromiseResult<ReturnType<TApiController[TEndpointKey]>>
      : never;
  };

  /**
   * Adds the config options to each endpoint within a controller (if it's a function within a controller, it's an endpoint)
   * @param query (optional) The query settings to apply to each endpoint, overrides the application settings, but can be overridden at hook level
   * @param mutation (optional) The mutation settings to apply to each endpoint, overrides the application settings, but can be overridden at hook level
   * @param request (optional) The request settings to apply to each endpoint, overrides the application settings, but can be overridden at hook level
   */
  type HookEndpointConfig<TApiController> = {
    [TEndpointKey in keyof TApiController]: TApiController[TEndpointKey] extends AnyFunction
      ? {
          query?: UseQueryConfigSettings<
            FirstParamOf<TApiController[TEndpointKey]>,
            PromiseResult<ReturnType<TApiController[TEndpointKey]>>
          >;
          mutation?: UseMutationSettings<FirstParamOf<TApiController[TEndpointKey]>>;
          request?: UseRequestSettings<FirstParamOf<TApiController[TEndpointKey]>>;
        }
      : never;
  };

  /**
   * Type denoting the settings object passed to the create method.
   * @param queryConfig The application level query settings, can be overridden at endpoint and hook execution level
   * @param mutationConfig The application level mutation settings, can be overridden at endpoint and hook execution level
   * @param requestConfig The application level settings for the useRequest hook, can be overridden at endpoint and hook execution level
   * @param generalConfig The application level general settings, can not be overridden, apply generally at application level
   * @param hookConfigFactory The factory function that creates the hook config library
   * @param mockEndpointFactory The factory function that creates the mock endpoint library
   * @param defaultDataFactory The factory function that creates the default data library
   */
  interface CreationSettings<TApiClient extends object, TParam extends object> {
    queryConfig?: UseQueryConfigSettings<TParam, any>;
    mutationConfig?: UseMutationSettings<TParam>;
    requestConfig?: UseRequestSettings<TParam>;
    generalConfig?: GeneralConfig;
    hookConfigFactory?: HookConfigLibraryFactory<TApiClient>;
    mockEndpointFactory?: MockEndpointLibraryFactory<TApiClient>;
    defaultDataFactory?: DefaultDataLibraryFactory<TApiClient>;
  }

  interface GeneralConfig {
    debugMode?: boolean;
  }

  /** LIVE RESPONSE TYPES */

  /** The type denoting the live response object returned from both the useQuery and useMutation hooks */
  export type LiveResponse<TCache> = { data?: TCache; error?: any; isFetching: boolean };

  /** CACHE KEY TYPES */

  export type CacheKey<TParam> = keyof TParam | ((param: TParam) => string | number);

  /** USE QUERY TYPES */

  /** The type of the useQuery hook, receives execution settings and returns an array containing the live state and a fetch method */
  interface UseQuery<TEndpoint extends AnyFunction> {
    (settings?: UseQuerySettings<FirstParamOf<TEndpoint>, PromiseResult<ReturnType<TEndpoint>>>): UseQueryResponse<
      PromiseResult<ReturnType<TEndpoint>>,
      Partial<FirstParamOf<TEndpoint>>
    >;
  }

  /**
   * The type denoting the response of the useQuery hook
   * @returns[0] An object containing the live data, error, and fetching bool relating to the API request.
   * @returns[1] A manual fetch method for invoking the request - receives the params, optional settings and returns a promise with some data
   */
  export type UseQueryResponse<TCache, TParam> = [
    LiveResponse<TCache>,
    (param?: TParam, fetchSettings?: UseQueryFetchSettings<TCache>) => void
  ];

  /**
   * The basic query settings used at system, application, endpoint and hook execution level.
   * @param autoInvoke Should the request fire when the component mounts? Will only fire if cache is stale - defaults to true at system level
   * @param invokeOnParamChange Should the request re-fire when the params change? - defaults to true at system level
   * @param caching The caching settings
   * @param useDefaultData Should the hook use the canned default data on initial render?
   * @param cacheKey (optional) A different string to use as the cache indexer, rather than the param hash, mostly for paging queries.
   * @param holdInvokeForCacheKeyParam (optional) Will hold any invocation until the parameter designated as the cacheKey has a non "falsey" value.
   * @param forceNetworkOnManualInvoke Should manual invocations hit the server by default? Can be over-ridden during a manualInvoke with the `forceNetwork` setting
   * @param parameters The parameters to send to the request
   * @param payloadModifier An optional function to modify the payload stored for this endpoint, receives any stored data as well as the incoming data.
   */
  export interface UseQuerySettings<TParam, TResponse> {
    autoInvoke?: boolean;
    invokeOnParamChange?: boolean;
    caching?: ApiHooksCaching.Settings<TParam>;
    useDefaultData?: boolean;
    cacheKey?: CacheKey<TParam>;
    holdInvokeForCacheKeyParam?: boolean;
    forceNetworkOnManualInvoke?: boolean;
    parameters?: TParam;
    payloadModifier?: (prevData: TResponse, newData: TResponse) => TResponse;
  }

  /**
   * The additional settings used at system, application level only - NOT hook execution
   * @param maxCachingDepth The maximum number of state slices to store (for different params) against this endpoint - defaults to 5 at system level
   */
  export interface UseQueryConfigSettings<TParam, TResponse> extends UseQuerySettings<TParam, TResponse> {
    maxCachingDepth?: number;
  }

  /**
   * The settings used by the 'fetch' method (returned as the second item in the array) from the useQuery hook
   * @param forceNetwork If true - cache status will be ignored and data re-requested from the server. Takes it's default from the `forceNetworkOnManualInvoke` query setting
   * @param payloadModifier An optional function to modify the payload stored for this endpoint, receives any stored data as well as the incoming data.
   */
  export interface UseQueryFetchSettings<TResponse> {
    forceNetwork?: boolean;
    payloadModifier?: (prevData: TResponse, newData: TResponse) => TResponse;
  }

  /** USE MUTATION TYPES */

  /** The type of the useMutation hook, receives execution settings and returns a fetch method and some live response state */
  interface UseMutation<TEndpoint extends AnyFunction> {
    (settings?: UseMutationSettings<FirstParamOf<TEndpoint>>): UseMutationResponse<
      PromiseResult<ReturnType<TEndpoint>>,
      Partial<FirstParamOf<TEndpoint>>
    >;
  }

  /**
   * The type denoting the response of the useMutation hook. receives the params + settings and returns a promise of the response data, and some live response data from local state NOT global cache
   * NOTE: The params and fetch settings here will override the params sent to the hook execution settings, but any hook execution params will be used if nothing is passed here.
   * @returns:
   * [0] - The fetch method used to send the request, returns a promise.
   * [1] - A simple "live response" object - not stored in global state.
   * [2] - A "refetch"
   */
  type UseMutationResponse<TResponse, TParam> = [
    (param?: TParam, fetchSettings?: UseMutationSettings<TParam>) => Promise<TResponse>,
    LiveResponse<TResponse>,
    (refetchQueries: EndpointIDs.Response<TParam>[]) => void
  ];

  /**
   * The basic mutation settings used at system, application, endpoint and hook execution level.
   * @param throwErrors (default=true) Should the request throw errors? Or swallow them, allowing them to be handled via the live response object?
   * @param fetchInBackground If true, the live response data and error will not be cleared when a new fetch is invoked.
   * @param parameters The parameters of the mutation request can be optionally defined here.
   * @param refetchQueries A set of endpoint IDs denoting queries to be re-fetched after the mutation has happened.
   */
  export interface UseMutationSettings<TParam> {
    throwErrors?: boolean;
    fetchInBackground?: boolean;
    parameters?: TParam;
    refetchQueries?: EndpointIDs.Response<TParam>[];
  }

  /** USE REQUEST TYPES */

  /** The type of the useRequest hook, receives execution settings and returns a fetch method detached from state and caching */
  interface UseRequest<TEndpoint extends AnyFunction> {
    (settings?: UseRequestSettings<Partial<FirstParamOf<TEndpoint>>>): UseRequestResponse<
      PromiseResult<ReturnType<TEndpoint>>,
      Partial<FirstParamOf<TEndpoint>>
    >;
  }

  /**
   * The basic request settings used at system, application, endpoint and hook execution level.
   */
  export interface UseRequestSettings<TParam> {
    parameters?: TParam;
  }

  /**
   * The type denoting the response of the useRequest hook. receives the params + settings and returns a promise of the response data detached from all state and caching.
   * NOTE: The params and fetch settings here will override the params sent to the hook execution settings, but any hook execution params will be used if nothing is passed here.
   */
  type UseRequestResponse<TResponse, TParam> = (
    param?: TParam,
    fetchSettings?: UseRequestSettings<TParam>
  ) => Promise<TResponse>;

  /** UTILITY FUNCTIONS */

  /**
   * Root logging function for debug mode, just a proxy for the console log for now.
   * @param messages The items to log
   */
  function log(...messages: any[]) {
    // eslint-disable-next-line no-console
    console.log(...messages);
  }

  /** CREATION FUNCTIONS */

  /**
   * Creates and empty dictionary of config for each endpoint to be populated outside this file at application level
   * @param apiClient The API client to parse and create an entry for each controller, and then another for each endpoint.
   * @returns The empty dictionary ready for populating in `../endpointSettings.ts` and `../mockEndpoints.ts`
   */
  function createEmptyHookLibraryDefaults<TApiClient extends object, TLibrary>(apiClient: TApiClient): TLibrary {
    return Object.keys(apiClient).reduce((controllerMemo, controllerKey) => {
      const newControllerMemo = { ...controllerMemo };
      newControllerMemo[controllerKey] = Object.keys(apiClient[controllerKey]).reduce((endpointMemo, endpointKey) => {
        const newEndpointMemo = { ...endpointMemo };
        newEndpointMemo[endpointKey] = {};
        return newEndpointMemo;
      }, {});
      return newControllerMemo;
    }, {} as TLibrary);
  }

  /**
   * Creates the useQuery and useMutation hooks for each endpoint in a controller
   * @param rootKey A string unique to the controller
   * @param controller The controller object
   * @param rootQuerySettings The query settings - system level with application level applied on top
   * @param rootMutationSettings The mutation settings - system level with application level applied on top
   * @param rootRequestSettings The settings for the useRequest hook - system level with application level applied on top
   * @param hookConfig The dictionary of config - populated in `../endpointSettings.ts`
   * @param mockEndpointLibrary The dictionary of mock endpoints - populated in `../mockEndpoints.ts`
   * @param defaultDataLibrary The dictionary of default data - populated in `../defaultData.ts`
   * @returns The useQuery and useMutation hooks
   */
  function createHooks<TController extends object>(
    rootKey: string,
    controller: TController,
    rootQuerySettings: UseQuerySettings<any, any>,
    rootMutationSettings: UseMutationSettings<any>,
    rootRequestSettings: UseRequestSettings<any>,
    hookConfig: HookConfigControllerLibrary<TController>,
    mockEndpointLibrary: MockEndpointControllerLibrary<TController>,
    defaultDataLibrary: DefaultDataControllerLibrary<TController>,
    generalConfig?: GeneralConfig
  ) {
    // Reduce the controller endpoints to produce an object for each one with the two hooks on it.
    return Object.keys(controller).reduce<EndpointHooks<TController>>((incomingControllerDictionary, endpointKey) => {
      // A string unique to the endpoint - combines the controller and endpoint names
      const endpointHash = `${rootKey}.${endpointKey}`;

      // fetch endpoint level query settings if available and apply on top of the passed in system and application level settings
      const querySettings: UseQueryConfigSettings<any, any> = hookConfig[endpointKey]?.query ?? {};
      const combinedQuerySettings = { ...rootQuerySettings, ...querySettings };

      // fetch endpoint level query caching settings if available and apply on top of the passed in system and application level settings
      combinedQuerySettings.caching = { ...rootQuerySettings.caching, ...(querySettings.caching ?? {}) };

      // fetch endpoint level query parameters if available and apply on top of the passed in system and application level settings
      combinedQuerySettings.parameters = {
        ...(rootQuerySettings.parameters ?? {}),
        ...(querySettings.parameters ?? {}),
      };

      // fetch endpoint level mutation settings if available and apply on top of the passed in system and application level settings
      const mutationSettings: UseMutationSettings<any> = hookConfig[endpointKey]?.mutation ?? {};
      const combinedMutationSettings = { ...rootMutationSettings, ...mutationSettings };

      // fetch endpoint level mutation refetch queries if available and apply on top of the passed in system and application level settings
      if (mutationSettings.refetchQueries) {
        combinedMutationSettings.refetchQueries = [
          ...(rootMutationSettings.refetchQueries || []),
          ...mutationSettings.refetchQueries,
        ];
      }

      // fetch endpoint level mutation parameters if available and apply on top of the passed in system and application level settings
      combinedMutationSettings.parameters = {
        ...(rootMutationSettings.parameters ?? {}),
        ...(mutationSettings.parameters ?? {}),
      };

      // fetch endpoint level request parameters if available and apply on top of the passed in system and application level settings
      const requestSettings: UseRequestSettings<any> = hookConfig[endpointKey]?.request ?? {};
      const combinedRequestSettings = { ...rootRequestSettings, ...requestSettings };

      // create two promise factories - one returns the actual endpoint promise, the other returns the mock endpoint if it's been created
      const promiseFactory = (arg: any) => controller[endpointKey](arg);
      const mockPromiseFactory =
        mockEndpointLibrary[endpointKey] &&
        ((arg: any, testKey?: string) => mockEndpointLibrary[endpointKey](arg, testKey));

      // store an initial application started date/time
      const applicationStartedTimestamp = Date.now();

      /**
       * Logs messages from useQuery hooks to the console if in debug mode
       * - includes some data about the controller and endpoint
       * - calls the root logging utility function
       */
      const queryLog = (...messages: any[]) => {
        if (generalConfig?.debugMode) {
          log(`API Hooks Query, Endpoint: ${endpointHash} -`, ...messages);
        }
      };
      /**
       * Logs messages from useMutation hooks to the console if in debug mode
       * - includes some data about the controller and endpoint
       * - calls the root logging utility function
       */
      const mutationLog = (...messages: any[]) => {
        if (generalConfig?.debugMode) {
          log(`API Hooks Mutation, Endpoint: ${endpointHash} -`, ...messages);
        }
      };
      /**
       * Logs messages from useRequest hooks to the console if in debug mode
       * - includes some data about the controller and endpoint
       * - calls the root logging utility function
       */
      const requestLog = (...messages: any[]) => {
        if (generalConfig?.debugMode) {
          log(`API Hooks Request, Endpoint: ${endpointHash} -`, ...messages);
        }
      };

      const controllerDictionary = { ...incomingControllerDictionary };
      controllerDictionary[endpointKey] = {
        /**
         * useQuery
         * - The hook to be used if the endpoint is a GET and should interface with the caching system
         * - Receives the hook execution level settings to override the system, application and hook level.
         * - Returns the live data and an optional fetch method to manually invoke
         * - Can only be used within a React Function Component
         */
        useQuery: (executionSettings: UseQuerySettings<any, any> = {}): UseQueryResponse<any, any> => {
          /** GATHER DATA AND SETTINGS */

          // listen to the context from the store - this state is updated via the central reducer - see `./store.tsx`
          const [state, dispatch, testKeys] = React.useContext(ApiHooksStore.Context);

          // settings - apply the hook execution settings (if any) to the passed in system, application and endpoint level.
          // NOTE - the JSON.stringify prevents the need for the consumer to memoize the incoming execution settings, it's not ideal, but it's only a small object so it should be ok.
          const settingsFromHook = React.useMemo<UseQueryConfigSettings<any, any>>(() => {
            const settings = { ...combinedQuerySettings, ...executionSettings };
            settings.caching = { ...combinedQuerySettings.caching, ...(executionSettings.caching ?? {}) };
            settings.parameters = { ...combinedQuerySettings.parameters, ...(executionSettings.parameters ?? {}) };
            return settings;
          }, [JSON.stringify(executionSettings)]);

          // in order to support very low cache timeouts or cache that's always stale, we need to make sure
          // we always return the data when it's literally just been loaded
          // this should only default to 'true' if we're using default data
          const justLoaded = React.useRef(!!settingsFromHook.useDefaultData);

          // cache key - retrieve any cache key value if one exists in the settings
          const cacheKeyValueFromHook = React.useMemo(() => {
            const { cacheKey, parameters } = settingsFromHook;
            return ApiHooksCaching.parseCacheKey(parameters, cacheKey);
          }, [settingsFromHook]);

          // parameters - create a unique string from the parameters used in the request - this allows us to detect whether the params of a request have changed and re-invoke
          const paramHashFromHook = React.useMemo<string>(() => {
            return ApiHooksCaching.hashParams(settingsFromHook.parameters);
          }, [settingsFromHook]);

          // storing the cache key in state means we can update it if the parameters change - either through the hook settings or a manual invoke
          const [cacheKey, setCacheKey] = React.useState<string>(cacheKeyValueFromHook);

          // store the last run params in a ref so they can be used in a refetch query.
          const lastUsedParams = React.useRef(settingsFromHook.parameters ?? {});

          // state - Fetch the state slice from the global state object (if exists) based on the cache key value if passed, or the default cache key
          const storedStateSlice = React.useMemo<ApiHooksStore.StateSlice<any>>(() => {
            const currentStoredStateSlice = state[endpointHash]?.[cacheKey];
            // check for default data settings and use that initially if appropriate.
            if (!currentStoredStateSlice && settingsFromHook.useDefaultData) {
              const defaultDataFactory = defaultDataLibrary[endpointKey];
              if (!defaultDataFactory) {
                throw new Error(
                  `API Hooks error - no default data has been defined for the following query: ${endpointHash}, did you mean to set "useDefaultData" to "true"?`
                );
              }
              const defaultDataValue = defaultDataFactory(settingsFromHook.parameters);
              queryLog("Using default data", { defaultData: defaultDataValue });
              return {
                paramHash: paramHashFromHook,
                data: defaultDataFactory(settingsFromHook.parameters),
                status: 'loaded',
                timestamp: applicationStartedTimestamp,
              };
            }
            return currentStoredStateSlice;
          }, [state[endpointHash], cacheKey, settingsFromHook]);

          /** CACHE READER */

          // value - create the data value to return from the state slice, this respects the caching settings to decide whether to return the actual data, but always returns any error or fetching status.
          const valueToReturn = React.useMemo<UseQueryResponse<any, any>[0]>(() => {
            // create two data responses - one without data, and one with
            const withoutData: UseQueryResponse<any, any>[0] = {
              error: storedStateSlice?.error,
              isFetching: storedStateSlice?.status === 'loading',
            };
            const withData = { ...withoutData, data: storedStateSlice?.data };

            // check whether the data is valid (not stale) depending on settings
            const valid =
              !!storedStateSlice?.data &&
              !ApiHooksCaching.isStale(storedStateSlice, settingsFromHook.caching.staleIfOlderThan);
            const validOnError =
              !!storedStateSlice?.data &&
              !ApiHooksCaching.isStale(storedStateSlice, settingsFromHook.caching.staleOnErrorIfOlderThan);
            const validIfFetching = !!storedStateSlice?.data && settingsFromHook.caching.fetchInBackground;

            queryLog("Current state loaded/updated", {
              settings: settingsFromHook,
              cacheData: storedStateSlice ?? 'none',
              cacheValidity: { valid, validOnError, validIfFetching },
              cacheKey,
            });

            // If the data has just finished loading then we need to return it regardless of the cache settings.
            // this allows us to support cache that is always stale.
            if (justLoaded.current) {
              justLoaded.current = false;
              return withData;
            }

            // return the data or not, depending on validity
            if (withoutData.isFetching) {
              return validIfFetching ? withData : withoutData;
            }
            if (withoutData.error) {
              return validOnError ? withData : withoutData;
            }
            return valid ? withData : withoutData;
          }, [storedStateSlice, settingsFromHook]);

          /** FETCHERS AND INVOKERS */

          // fetcher - the fetch method - attempts to retrieve data from server, regardless of caching
          const fetch = React.useCallback<(fetchSettings: typeof settingsFromHook, finalParamHash: string) => void>(
            async (fetchSettings, finalParamHash) => {
              // get the new cache key if applicable, and set it to state, thus potentially updating the state slice returned from the hook.
              const finalCacheKey = ApiHooksCaching.parseCacheKey(fetchSettings.parameters, fetchSettings.cacheKey);
              setCacheKey(finalCacheKey);

              // check the global live fetching log to avoid simultaneous requests being fired before react has processed the state changes.
              if (ApiHooksGlobal.isFetching(endpointHash, finalCacheKey)) {
                queryLog('Fetching aborted, request already in progress', {
                  settings: fetchSettings,
                  paramHash: finalParamHash,
                  cacheKey: finalCacheKey,
                });
                return;
              }
              // set the endpoint to "fetching" in the live fetching log, to prevent duplicate requests from being fired on component load.
              ApiHooksGlobal.setFetching(endpointHash, finalCacheKey, true);

              queryLog('Fetching', { settings: fetchSettings, paramHash: finalParamHash, cacheKey: finalCacheKey });

              // dispatch the loading action to change the fetching state
              dispatch(
                ApiHooksStore.Actions.loading(
                  endpointHash,
                  finalParamHash,
                  finalCacheKey,
                  fetchSettings.maxCachingDepth
                )
              );

              // set up a try/catch - we're about to make the actual request
              let value: any;
              try {
                // fetch the data value from either the real or mock endpoint, depending on the settings
                if (testKeys) {
                  if (!mockPromiseFactory) {
                    throw new Error(
                      `API Hooks error - no mock endpoint has been defined for the following query: ${endpointHash}`
                    );
                  }
                  value = await mockPromiseFactory(fetchSettings.parameters, testKeys[endpointHash]?.testKey);
                } else {
                  value = await promiseFactory(fetchSettings.parameters);
                }
                // send the data to the store by despatching the loaded action
                queryLog('Fetch successful, with result:', value);
                justLoaded.current = true;
                lastUsedParams.current = fetchSettings.parameters;
                dispatch(
                  ApiHooksStore.Actions.loaded(
                    endpointHash,
                    finalParamHash,
                    finalCacheKey,
                    fetchSettings.payloadModifier
                      ? fetchSettings.payloadModifier(storedStateSlice?.data, value)
                      : value,
                    fetchSettings.maxCachingDepth
                  )
                );
              } catch (error) {
                // an error has been thrown by the server, catch it and set it in state, otherwise throw it to the consumer.
                queryLog('Fetch failed, with error:', error);
                dispatch(
                  ApiHooksStore.Actions.error(
                    endpointHash,
                    finalParamHash,
                    finalCacheKey,
                    error,
                    fetchSettings.maxCachingDepth
                  )
                );
              } finally {
                // set the request as finished fetching in the live fetching log so that future requests won't be aborted.
                ApiHooksGlobal.setFetching(endpointHash, finalCacheKey, false);
              }
            },
            [dispatch, setCacheKey, storedStateSlice, testKeys]
          );

          // invoke - called when the component mounts if autoInvoke = true, and from the manual invoke method
          // checks whether data should be fetched based on cache settings, error status & whether the params have changed
          const invoke = React.useCallback<UseQueryResponse<any, any>[1]>(
            (param, fetchSettings) => {
              // merge any params into existing settings passed from higher levels
              const finalSettings = {
                ...settingsFromHook,
                ...(param ? { parameters: { ...(settingsFromHook.parameters ?? {}), ...param } } : {}),
              };

              // if the manual invoke has been used with a payload modifier, add it on here.
              if (fetchSettings?.payloadModifier) {
                finalSettings.payloadModifier = fetchSettings.payloadModifier;
              }

              // read force network setting, from query setting first, then fetch settings if present.
              const forceNetwork = fetchSettings?.forceNetwork;

              // check for bookmark parameters, and read the stored value if appropriate
              if (finalSettings.caching?.bookmarkParameters && valueToReturn?.data && storedStateSlice?.paramHash) {
                // eslint-disable-next-line no-unsafe-optional-chaining
                for (const bookmark of finalSettings.caching?.bookmarkParameters) {
                  if (!finalSettings.parameters?.[bookmark]) {
                    const parsedHash = JSON.parse(storedStateSlice.paramHash);
                    if (parsedHash?.[bookmark]) {
                      finalSettings.parameters = {
                        ...(finalSettings.parameters || {}),
                        [bookmark]: parsedHash[bookmark],
                      };
                      queryLog('Loaded stored bookmark param', {
                        paramName: bookmark,
                        storedValue: parsedHash[bookmark],
                      });
                    }
                  }
                }
              }

              // create a new param hash for comparison, we should make sure we invoke if the params are different to what's cached.
              const newParamHash = ApiHooksCaching.hashParams(finalSettings.parameters);

              // create a set of booleans containing information about the current state of the request/caching.
              const inErrorState = !!storedStateSlice?.error;
              const cacheIsStaleOrAbsent = !valueToReturn?.data;
              const alreadyFetching = !!valueToReturn?.isFetching;
              const paramsAreDifferent = !finalSettings.invokeOnParamChange
                ? false
                : !!storedStateSlice && storedStateSlice.paramHash !== newParamHash;

              // The logic which dictates whether to invoke a request, or whether we can use what we already have in cache.
              const shouldLoad =
                (cacheIsStaleOrAbsent && !alreadyFetching) || inErrorState || paramsAreDifferent || forceNetwork;

              queryLog(shouldLoad ? 'Invoking fetcher' : 'Cache loaded', {
                inErrorState,
                cacheIsStaleOrAbsent,
                alreadyFetching,
                paramsAreDifferent,
                forceNetwork,
              });

              if (shouldLoad) {
                fetch(finalSettings, newParamHash);
              }
            },
            [storedStateSlice, valueToReturn, settingsFromHook, fetch]
          );

          // the manual invoke method is really just a proxy for invoke, with a bit of logging and settings application. returned as index 1 of the hook response for manual fetching
          const manualInvoke = React.useCallback<UseQueryResponse<any, any>[1]>(
            (...args) => {
              queryLog('Manual invoke triggered');
              // apply default forceNetwork setting to manual invoke
              const newArgs = [...args];
              if (newArgs[1]?.forceNetwork === undefined) {
                newArgs[1] = { ...(newArgs[1] || {}), forceNetwork: settingsFromHook.forceNetworkOnManualInvoke };
              }
              invoke(...newArgs);
            },
            [invoke]
          );

          /** INVOCATION TRIGGER EFFECTS */

          // called when the component mounts, checks whether to invoke based on settings
          useDidMountEffect(() => {
            if (settingsFromHook.autoInvoke) {
              queryLog('Auto invoke triggered');
              if (
                settingsFromHook.holdInvokeForCacheKeyParam &&
                settingsFromHook.cacheKey &&
                ApiHooksCaching.cacheKeyIsDefault(settingsFromHook.parameters, settingsFromHook.cacheKey)
              ) {
                queryLog('Invoke held - cache key property has falsy value', { settings: settingsFromHook });
              } else {
                invoke();
              }
            }
          });

          // called when the params passed into the hook CHANGE, but NOT on first run - sets the new params/cache key and fetches data if the settings allow.
          useDidUpdateEffect(() => {
            queryLog('Parameters changed', { oldParams: storedStateSlice?.paramHash, newParams: paramHashFromHook });
            setCacheKey(ApiHooksCaching.parseCacheKey(settingsFromHook.parameters, settingsFromHook.cacheKey));
            if (settingsFromHook.invokeOnParamChange) {
              if (
                settingsFromHook.holdInvokeForCacheKeyParam &&
                settingsFromHook.cacheKey &&
                ApiHooksCaching.cacheKeyIsDefault(settingsFromHook.parameters, settingsFromHook.cacheKey)
              ) {
                queryLog('Fetch held - cache key property has falsy value', { settings: settingsFromHook });
              } else {
                fetch(settingsFromHook, paramHashFromHook);
              }
            }
          }, [paramHashFromHook]);

          /** REFETCH QUERY TRIGGER EFFECTS */

          // called when the entire cache is reset by a refetch query passed into a mutation
          useDidUpdateEffect(() => {
            if (!storedStateSlice) {
              queryLog('Cache reset - refetch triggered', { parameters: lastUsedParams.current });
              invoke(lastUsedParams.current);
            }
          }, [storedStateSlice]);

          /** RETURN FROM HOOK */

          // return the state value and the fetch method from the hook
          return [valueToReturn, manualInvoke];
        },
        /**
         * useMutation
         * - The hook to be used if the endpoint is a POST/PUT/DELETE and should NOT interface with the caching system
         * - Receives the hook execution level settings to override the system, application and hook level.
         * - Returns the fetch method to manually invoke, and a live response object
         * - Can only be used within a React Function Component
         */
        useMutation: (executionSettings: UseMutationSettings<any> = {}): UseMutationResponse<any, any> => {
          // store response in state to return as index 1 from the hook
          const [liveResponse, setLiveResponse] = React.useState<UseMutationResponse<any, any>[1]>({
            isFetching: false,
          });

          // get the dispatcher and test keys from context
          const [, dispatch, testKeys] = React.useContext(ApiHooksStore.Context);

          // settings - apply the hook execution settings (if any) to the passed in system, application and endpoint level.
          // NOTE - the JSON.stringify prevents the need for the consumer to memoize the incoming execution settings, it's not ideal, but it's only a small object so it should be ok.
          const settingsFromHook = React.useMemo<UseQueryConfigSettings<any, any>>(() => {
            const settings = { ...combinedMutationSettings, ...executionSettings };
            settings.parameters = { ...combinedMutationSettings.parameters, ...(executionSettings.parameters ?? {}) };
            if (executionSettings.refetchQueries) {
              settings.refetchQueries = [
                ...(combinedMutationSettings.refetchQueries ?? []),
                ...executionSettings.refetchQueries,
              ];
            }
            return settings;
          }, [JSON.stringify(executionSettings)]);

          // the method used to dispatch resets - these resets trigger the "refetch query" behaviour.
          const refetchQueries = React.useCallback<UseMutationResponse<any, any>[2]>(
            queries => {
              for (const query of queries) {
                let finalCacheKeyValue: string | number;
                try {
                  finalCacheKeyValue = ApiHooksCaching.cacheKeyValueFromRefetchQuery(
                    settingsFromHook.parameters,
                    query
                  );
                } catch (error) {
                  throw new Error(
                    `API Hooks Mutation Error, Endpoint: ${endpointHash} - ${error?.message ?? 'Refetch query failed'}`
                  );
                }
                mutationLog("Refetch query processed", { query, finalCacheKeyValue });
                dispatch(ApiHooksStore.Actions.reset(query.endpointHash, finalCacheKeyValue?.toString()));
              }
            },
            [dispatch, settingsFromHook]
          );

          // fetch method - detached from cache, calls API and returns a promise
          const fetch = React.useCallback<UseMutationResponse<any, any>[0]>(
            async (param, settings) => {
              // combine all the settings together in order to include system, application, endpoint, execution and fetch level, as well as the passed in params.
              const finalSettings: UseMutationSettings<any> = {
                ...settingsFromHook,
                ...(settings || {}),
                ...(param
                  ? { parameters: isFormData(param) ? param : { ...(settingsFromHook.parameters ?? {}), ...param } }
                  : {}),
              };

              // set live response to loading
              const loadingStateResponse = {
                ...(finalSettings.fetchInBackground ? liveResponse : {}),
                isFetching: true,
              };
              mutationLog("Fetch started", { finalSettings, liveResponse: loadingStateResponse });
              setLiveResponse(loadingStateResponse);

              // fetch the data value from either the real or mock endpoint, depending on the settings
              let value: any;
              try {
                if (testKeys) {
                  if (!mockPromiseFactory) {
                    throw new Error(
                      `API Hooks error - no mock endpoint has been defined for the following mutation: ${endpointHash}`
                    );
                  }
                  value = await mockPromiseFactory(finalSettings.parameters, testKeys[endpointKey]?.testKey);
                } else {
                  value = await promiseFactory(finalSettings.parameters);
                }
                // set live response to success
                const successResponse = { isFetching: false, data: value };
                mutationLog("Fetch successful", { finalSettings, response: value, liveResponse: successResponse });
                setLiveResponse(successResponse);
                // handle any refetch queries that were passed in.
                if (finalSettings.refetchQueries) {
                  const resolvedRefetchQueries = finalSettings.refetchQueries.map(query => {
                    return {
                      ...query,
                      cacheKeyValue: ApiHooksCaching.cacheKeyValueFromRefetchQuery(finalSettings.parameters, query),
                    };
                  });
                  refetchQueries(resolvedRefetchQueries);
                }
              } catch (error) {
                // set live response to failed
                const failedResponse = { isFetching: false, error, data: undefined };
                mutationLog("Fetch failed", { error, failedResponse });
                setLiveResponse(failedResponse);
                if (finalSettings.throwErrors) {
                  throw error;
                }
              }
              // return the data, errors will be thrown for mutations and should be handled by the consuming component unless `throwErrors` is explicitly set to false in settings.
              return value;
            },
            [settingsFromHook, liveResponse, refetchQueries]
          );

          return [fetch, liveResponse, refetchQueries];
        },
        /**
         * useRequest
         * - The hook to be used for any request for which we ONLY want the promise, no local state or caching at all.
         * - Receives the hook execution level settings to override the system, application and hook level.
         * - Returns the detached fetch method to manually invoke
         * - Can only be used within a React Function Component
         */
        useRequest: (executionSettings: UseRequestSettings<any> = {}): UseRequestResponse<any, any> => {
          // settings - apply the hook execution settings (if any) to the passed in system, application and endpoint level.
          // NOTE - the JSON.stringify prevents the need for the consumer to memoize the incoming execution settings, it's not ideal, but it's only a small object so it should be ok.
          const settingsFromHook = React.useMemo<UseRequestSettings<any>>(() => {
            const settings = { ...combinedRequestSettings, ...executionSettings };
            settings.parameters = { ...combinedMutationSettings.parameters, ...(executionSettings.parameters ?? {}) };
            return settings;
          }, [JSON.stringify(executionSettings)]);

          // get any test keys
          const [, , testKeys] = React.useContext(ApiHooksStore.Context);

          // fetch method - detached from cache, calls API and returns a promise
          const fetch = React.useCallback<UseRequestResponse<any, any>>(
            async (param, settings) => {
              // combine all the settings together in order to include system, application, endpoint, execution and fetch level, as well as the passed in params.
              const finalSettings: UseRequestSettings<any> & { parameters?: any } = {
                ...settingsFromHook,
                ...(settings || {}),
                ...(param
                  ? { parameters: isFormData(param) ? param : { ...(settingsFromHook.parameters ?? {}), ...param } }
                  : {}),
              };

              requestLog("Fetch started", { finalSettings });

              // fetch the data value from either the real or mock endpoint, depending on the settings
              let value: any;
              try {
                if (testKeys) {
                  if (!mockPromiseFactory) {
                    throw new Error(
                      `API Hooks error - no mock endpoint has been defined for the following request: ${endpointHash}`
                    );
                  }
                  value = await mockPromiseFactory(finalSettings.parameters, testKeys[endpointKey]?.testKey);
                } else {
                  value = await promiseFactory(finalSettings.parameters);
                }
                requestLog("Fetch successful", { finalSettings, response: value });
              } catch (error) {
                // set live response to failed
                requestLog("Fetch failed", { finalSettings, error });
                throw error;
              }
              // return the data, errors will be thrown for requests and should be handled by the consuming components.
              return value;
            },
            [settingsFromHook, testKeys]
          );

          return fetch;
        },
      };
      return controllerDictionary;
    }, {} as EndpointHooks<TController>);
  }

  /**
   * The create function takes an API client and returns the hooks, config dictionaries, and mock endpoints dictionary.
   * @param apiClient The API client to parse, must be an object containing controller objects with nested endpoint functions
   * @param config The application level config for the useQuery and useMutation hooks. Overrides the system config but can potentially be overridden at endpoint and hook level
   * @returns The hooks
   */
  export function create<TApiClient extends object = any>(
    apiClient: TApiClient,
    config: CreationSettings<TApiClient, any> = {}
  ): ControllerHooks<TApiClient> {
    if (config?.generalConfig?.debugMode) {
      log('API Hooks - creating hook/config library for (client/applicationConfig):', apiClient, config);
    }

    // apply application level query settings onto system level if any
    const rootQuerySettings: UseQueryConfigSettings<any, any> = {
      ...ApiHooksSystemSettings.systemDefaultQuery,
      ...(config.queryConfig ?? {}),
    };
    // apply application level query caching settings onto system level if any
    rootQuerySettings.caching = { ...ApiHooksCaching.systemDefaults, ...(config.queryConfig?.caching ?? {}) };
    // apply application level mutation settings onto system level if any
    const rootMutationSettings: UseMutationSettings<any> = {
      ...ApiHooksSystemSettings.systemDefaultMutation,
      ...(config.mutationConfig ?? {}),
    };
    // apply application level request settings onto system level if any
    const rootRequestSettings: UseRequestSettings<any> = {
      ...ApiHooksSystemSettings.systemDefaultRequest,
      ...(config.requestConfig ?? {}),
    };

    // create empty library or controller/endpoint objects to pass to settings factory and mock endpoint factory.
    const emptyHookConfigLibrary: HookConfigControllerLibrary<TApiClient> = createEmptyHookLibraryDefaults(apiClient);
    const emptyMockEndpointLibrary: MockEndpointControllerLibrary<TApiClient> =
      createEmptyHookLibraryDefaults(apiClient);
    const emptyDefaultDataLibrary: DefaultDataControllerLibrary<TApiClient> = createEmptyHookLibraryDefaults(apiClient);

    // create hook config object from factory function
    const hookConfig = config?.hookConfigFactory?.(emptyHookConfigLibrary) ?? emptyHookConfigLibrary;
    const mockEndpoints = config?.mockEndpointFactory?.(emptyMockEndpointLibrary) ?? emptyMockEndpointLibrary;
    const defaultData = config?.defaultDataFactory?.(emptyDefaultDataLibrary) ?? emptyDefaultDataLibrary;

    // Reduce client controller dictionary into hooks and config
    return Object.keys(apiClient).reduce<ControllerHooks<TApiClient>>((memo, key) => {
      const newMemo = { ...memo };
      const controller = apiClient[key];
      newMemo[key] = createHooks(
        key,
        controller,
        rootQuerySettings,
        rootMutationSettings,
        rootRequestSettings,
        hookConfig[key],
        mockEndpoints[key],
        defaultData[key],
        config?.generalConfig
      );
      return newMemo;
    }, {} as ControllerHooks<TApiClient>);
  }
}
